import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getAssessmentSession, GetAssessmentSessionOutputType, saveAssessmentScore, completeAssessment, pauseAssessment, resumeAssessment } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle, Shield, User, BookOpen } from 'lucide-react';
import CriterionCard from '@/components/training/CriterionCard';
import AssessmentOutcomeSheet from '@/components/training/AssessmentOutcomeSheet';

type SessionData = GetAssessmentSessionOutputType;
type Score = 'competent' | 'not_yet' | 'not_observed';

export default function InvigilatorAssessment() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scores, setScores] = useState<Record<string, Score>>({});
  const [showOutcome, setShowOutcome] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadSession = useCallback(async () => {
    if (!sessionId) return;
    try {
      const d = await getAssessmentSession({ sessionId });
      setData(d);
      setScores(d.scores as Record<string, Score>);
    } catch (e: any) {
      setError(e.message || 'Failed to load assessment');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { if (user) loadSession(); }, [user, loadSession]);

  const handleScore = async (idx: number, score: Score) => {
    if (!sessionId || !data?.canAssess) return;
    const prev = { ...scores };
    setScores(s => ({ ...s, [String(idx)]: score }));
    try {
      await saveAssessmentScore({ sessionId, criterionIndex: idx, score });
    } catch {
      setScores(prev);
      toast.error('Failed to save score');
    }
  };

  const handleEvidenceSaved = () => { loadSession(); };

  const totalCriteria = data?.criteria.length || 0;
  const scoredCount = Object.keys(scores).length;
  const competentCount = Object.values(scores).filter(s => s === 'competent').length;
  const notYetCount = Object.values(scores).filter(s => s === 'not_yet').length;
  const allScored = scoredCount >= totalCriteria && totalCriteria > 0;
  const anyNotYet = notYetCount > 0;

  if (loading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <AlertTriangle className="w-10 h-10 text-destructive mx-auto mb-3" />
        <p className="text-sm text-muted-foreground mb-4">{error || 'Session not found'}</p>
        <Link to="/train" className="text-sm text-primary hover:underline">← Back to Training</Link>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto pb-32">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-card sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Link to="/train" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-primary uppercase tracking-wider">Practical Assessment</p>
            <h1 className="text-sm font-bold text-foreground truncate">{data.module.displayLabel}</h1>
          </div>
          <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
            data.session.status === 'Approved' ? 'bg-primary/20 text-primary' :
            data.session.status === 'Further Training' ? 'bg-destructive/20 text-destructive' :
            'bg-orange-500/20 text-orange-600'
          }`}>
            {data.session.status}
          </div>
        </div>
      </div>

      {/* Trainee info */}
      <div className="px-4 py-3 bg-muted/30 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">{data.trainee.name}</p>
            <p className="text-xs text-muted-foreground">{data.trainee.department} · {data.trainee.role}</p>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <BookOpen className="w-3.5 h-3.5" />
          <span>{data.course.title} ({data.course.courseCode})</span>
        </div>
      </div>

      {/* Safety warnings */}
      {data.module.safetyWarnings && (
        <div className="mx-4 mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
          <div className="flex items-start gap-2">
            <Shield className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-destructive mb-0.5">Safety Warning</p>
              <p className="text-xs text-destructive/80">{data.module.safetyWarnings}</p>
            </div>
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
          <span>{scoredCount} of {totalCriteria} criteria assessed</span>
          <span className="flex items-center gap-2">
            <span className="flex items-center gap-0.5"><CheckCircle className="w-3 h-3 text-primary" />{competentCount}</span>
            <span className="flex items-center gap-0.5"><XCircle className="w-3 h-3 text-destructive" />{notYetCount}</span>
          </span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-2 bg-primary rounded-full transition-all" style={{ width: `${totalCriteria > 0 ? (scoredCount / totalCriteria) * 100 : 0}%` }} />
        </div>
      </div>

      {/* Learning outcome */}
      <div className="px-4 py-2">
        <p className="text-xs text-muted-foreground italic">&quot;{data.module.learningOutcome}&quot;</p>
      </div>

      {/* Criteria list */}
      <div className="px-4 py-2 space-y-3">
        {data.criteria.map((criterion) => (
          <CriterionCard
            key={criterion.index}
            criterion={criterion}
            score={scores[String(criterion.index)] as Score | undefined}
            evidence={data.evidence.filter(e => e.criterionIndex === criterion.index)}
            canAssess={data.canAssess}
            sessionId={sessionId!}
            onScore={(score) => handleScore(criterion.index, score)}
            onEvidenceSaved={handleEvidenceSaved}
          />
        ))}

        {data.criteria.length === 0 && (
          <div className="py-8 text-center">
            <AlertTriangle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No practical assessment criteria configured for this module.</p>
          </div>
        )}
      </div>

      {/* Corrective actions display */}
      {data.correctiveActions.length > 0 && (
        <div className="px-4 py-3 border-t border-border mt-4">
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Corrective Actions</h3>
          {data.correctiveActions.map(ca => (
            <div key={ca.id} className="p-3 bg-destructive/5 border border-destructive/20 rounded-xl mb-2">
              <p className="text-sm font-medium text-foreground">{ca.actionTitle}</p>
              {ca.coachingGiven && <p className="text-xs text-muted-foreground mt-1">Coaching: {ca.coachingGiven}</p>}
              {ca.actionsRequired && <p className="text-xs text-muted-foreground mt-1">Actions: {ca.actionsRequired}</p>}
              <span className={`inline-block mt-1.5 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                ca.status === 'Resolved' ? 'bg-primary/20 text-primary' : 'bg-orange-500/20 text-orange-600'
              }`}>{ca.status}</span>
            </div>
          ))}
        </div>
      )}

      {/* Bottom action bar */}
      {data.canAssess && totalCriteria > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-card border-t border-border safe-area-pb z-30">
          <div className="max-w-lg mx-auto space-y-2">
            <Button
              onClick={() => setShowOutcome(true)}
              disabled={!allScored}
              className="w-full h-12 text-sm font-semibold touch-manipulation"
            >
              {allScored ? (anyNotYet ? 'Complete — Further Training Required' : 'Complete — Approve Module') : `Score all ${totalCriteria} criteria to complete`}
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                if (!sessionId) return;
                try {
                  const result = await pauseAssessment({ sessionId });
                  toast.success(result.message);
                  navigate('/train');
                } catch (e: any) {
                  toast.error(e.message || 'Failed to pause');
                }
              }}
              className="w-full h-10 text-xs font-medium touch-manipulation"
            >
              Save &amp; Pause Assessment
            </Button>
          </div>
        </div>
      )}

      {/* Paused session — Resume button */}
      {data.session.status === 'Paused' && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-card border-t border-border safe-area-pb z-30">
          <div className="max-w-lg mx-auto">
            <Button
              onClick={async () => {
                if (!sessionId) return;
                try {
                  await resumeAssessment({ sessionId });
                  toast.success('Assessment resumed');
                  loadSession();
                } catch (e: any) {
                  toast.error(e.message || 'Failed to resume');
                }
              }}
              className="w-full h-12 text-sm font-semibold touch-manipulation"
            >
              Resume Assessment
            </Button>
          </div>
        </div>
      )}

      {/* Outcome sheet */}
      {showOutcome && (
        <AssessmentOutcomeSheet
          anyNotYet={anyNotYet}
          moduleName={data.module.displayLabel}
          traineeName={data.trainee.name}
          submitting={submitting}
          onClose={() => setShowOutcome(false)}
          onSubmit={async (outcome, notes, actions, pin) => {
            if (!sessionId) return { success: false, message: 'No session' };
            setSubmitting(true);
            try {
              const clientMutationId = crypto.randomUUID();
              const result = await completeAssessment({
                sessionId,
                clientMutationId,
                outcome,
                pin,
                correctiveActionNotes: notes,
                correctiveActions: actions,
              });
              if (result.success) {
                toast.success(result.message);
                setShowOutcome(false);
                loadSession();
              }
              return {
                success: result.success,
                message: result.message,
                pinAttemptsRemaining: result.pinAttemptsRemaining,
                lockedUntil: result.lockedUntil,
              };
            } catch (e: any) {
              return { success: false, message: e.message || 'Failed to complete assessment' };
            } finally {
              setSubmitting(false);
            }
          }}
        />
      )}
    </div>
  );
}
